import {
  HHButton,
  HHLoadingButton,
  HHStack,
  HHTextField,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Autocomplete,
  FormControlLabel,
  InputAdornment,
  Switch,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDynamicDrawerContext } from '../../../context/dynamic-drawer.context';
import {
  FeeScheduleEntityType,
  PaymentSystemType,
  PaymentType,
} from '../../../types';
import { getCurrencySymbol } from '../../../utils/currency-helpers';
import {
  DEFAULT_FEE_SCHEDULE_FORM_VALUES,
  FeeScheduleFormValues,
  FeeScheduleValidationSchema,
} from './validation-schema';

export const FEE_SCHEDULE_FORM_TEST_ID = 'fee-schedule-form';
export const FEE_SCHEDULE_FORM_CANCEL_TEST_ID =
  'fee-schedule-form-cancel-button';
export const FEE_SCHEDULE_FORM_SUBMIT_TEST_ID =
  'fee-schedule-form-submit-button';
export const FEE_SCHEDULE_FORM_ENTITY_INPUT_TEST_ID =
  'fee-schedule-form-entity-input';
export const FEE_SCHEDULE_FORM_PAYMENT_TYPE_INPUT_TEST_ID =
  'fee-schedule-form-payment-type-input';
export const FEE_SCHEDULE_FORM_PAYMENT_SYSTEM_INPUT_TEST_ID =
  'fee-schedule-form-payment-system-input';
export const FEE_SCHEDULE_FORM_PRICE_INPUT_TEST_ID =
  'fee-schedule-form-price-input';
export const FEE_SCHEDULE_FORM_ENABLED_INPUT_TEST_ID =
  'fee-schedule-form-enabled-input';
export const FEE_SCHEDULE_FORM_NOTES_INPUT_TEST_ID =
  'fee-schedule-form-notes-input';

export interface EntityOption {
  id: number;
  name: string;
}

interface FeeScheduleFormProps {
  onSubmit: (values: FeeScheduleFormValues) => void | Promise<void>;
  initialValues?: FeeScheduleFormValues;
  entityType: FeeScheduleEntityType;
  entities: EntityOption[];
  entitiesLoading: boolean;
  paymentTypes: PaymentType[];
  paymentTypesLoading: boolean;
}

const paymentSystemOptions = Object.values(PaymentSystemType);

export const FeeScheduleForm = ({
  onSubmit,
  initialValues,
  entityType,
  entities,
  entitiesLoading,
  paymentTypes,
  paymentTypesLoading,
}: FeeScheduleFormProps): JSX.Element => {
  const { onClose } = useDynamicDrawerContext();

  const paymentTypeOptions = useMemo(
    () =>
      [...paymentTypes].map(pt => pt.code).sort((a, b) => a.localeCompare(b)),
    [paymentTypes],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting },
    reset,
  } = useForm<FeeScheduleFormValues>({
    mode: 'onChange',
    resolver: yupResolver(FeeScheduleValidationSchema),
    defaultValues: DEFAULT_FEE_SCHEDULE_FORM_VALUES,
  });

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const handleFormSubmit = async (
    values: FeeScheduleFormValues,
  ): Promise<void> => {
    await onSubmit(values);
  };

  const isEditMode = !!initialValues;
  const sortedEntities = useMemo(
    () => [...entities].sort((a, b) => a.name.localeCompare(b.name)),
    [entities],
  );

  return (
    <form data-testid={FEE_SCHEDULE_FORM_TEST_ID}>
      <HHStack spacing={6} direction="column">
        <Controller
          control={control}
          name="entityId"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              value={sortedEntities.find(entity => entity.id === value) ?? null}
              options={sortedEntities}
              getOptionLabel={(option): string => option.name}
              isOptionEqualToValue={(option, val): boolean =>
                option.id === val?.id
              }
              onChange={(_, newValue): void => onChange(newValue?.id ?? null)}
              disabled={isEditMode}
              loading={entitiesLoading}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="outlined"
                  label={
                    entityType === FeeScheduleEntityType.Client
                      ? 'Client'
                      : 'Partnership'
                  }
                  error={!!errors.entityId}
                  helperText={errors.entityId?.message}
                  inputProps={{ ...params.inputProps, name: 'entityId' }}
                  data-testid={FEE_SCHEDULE_FORM_ENTITY_INPUT_TEST_ID}
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name="paymentType"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              value={value || null}
              options={paymentTypeOptions}
              onChange={(_, newValue): void => onChange(newValue ?? '')}
              loading={paymentTypesLoading}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="outlined"
                  label="Payment Type"
                  error={!!errors.paymentType}
                  helperText={errors.paymentType?.message}
                  inputProps={{ ...params.inputProps, name: 'paymentType' }}
                  data-testid={FEE_SCHEDULE_FORM_PAYMENT_TYPE_INPUT_TEST_ID}
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name="paymentSystem"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              value={value ?? null}
              options={paymentSystemOptions}
              onChange={(_, newValue): void => onChange(newValue ?? null)}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="outlined"
                  label="Payment System"
                  error={!!errors.paymentSystem}
                  helperText={errors.paymentSystem?.message}
                  inputProps={{ ...params.inputProps, name: 'paymentSystem' }}
                  data-testid={FEE_SCHEDULE_FORM_PAYMENT_SYSTEM_INPUT_TEST_ID}
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name="price"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="Price"
              value={value ?? ''}
              onChange={onChange}
              error={!!errors.price}
              helperText={errors.price?.message}
              type="number"
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    disableTypography
                    sx={{ color: 'gray' }}
                  >
                    {getCurrencySymbol()}
                  </InputAdornment>
                ),
              }}
              inputProps={{
                inputMode: 'numeric',
                min: '0',
                name: 'price',
              }}
              data-testid={FEE_SCHEDULE_FORM_PRICE_INPUT_TEST_ID}
            />
          )}
        />

        <Controller
          control={control}
          name="enabled"
          render={({ field: { onChange, value } }): JSX.Element => (
            <FormControlLabel
              control={
                <Switch
                  checked={value}
                  onChange={(e): void => onChange(e.target.checked)}
                  inputProps={{
                    role: 'switch',
                    name: 'enabled',
                  }}
                  data-testid={FEE_SCHEDULE_FORM_ENABLED_INPUT_TEST_ID}
                />
              }
              label="Enabled"
            />
          )}
        />

        <Controller
          control={control}
          name="notes"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="Notes"
              value={value}
              onChange={onChange}
              multiline
              rows={3}
              inputProps={{ role: 'textbox', name: 'notes' }}
              data-testid={FEE_SCHEDULE_FORM_NOTES_INPUT_TEST_ID}
            />
          )}
        />

        <HHStack direction="row" spacing={2} justifyContent="flex-end">
          <HHButton
            hhVariant="variant-bypass"
            variant="text"
            onClick={handleCancel}
            type="button"
            disabled={isSubmitting}
            data-testid={FEE_SCHEDULE_FORM_CANCEL_TEST_ID}
          >
            Cancel
          </HHButton>
          <HHLoadingButton
            hhVariant="variant-bypass"
            variant="contained"
            type="submit"
            disabled={!isValid || !isDirty || isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit(handleFormSubmit)}
            data-testid={FEE_SCHEDULE_FORM_SUBMIT_TEST_ID}
          >
            Submit
          </HHLoadingButton>
        </HHStack>
      </HHStack>
    </form>
  );
};
